import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { ELECTRICITY_BILLING_MODE_LABELS } from "@/lib/articleConstants";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type ElectricityBillingMode,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

const MODE_HINTS: Record<ElectricityBillingMode, string> = {
  flat_per_night: "Fester Betrag pro Nacht, wenn der Gast Strom wünscht.",
  per_kwh:
    "Strom wird erst bei Rechnungserstellung nach tatsächlichem Verbrauch berechnet. Legen Sie eine Zuordnung „Strom pro kWh“ an.",
  flat_with_included_kwh:
    "Pro Nacht eine Pauschale plus Abrechnung für Mehrverbrauch über die inkludierte kWh-Menge hinaus.",
};

export function ElectricityBillingSection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [mode, setMode] = useState<ElectricityBillingMode>(settings.electricity_billing.mode);
  const [includedKwh, setIncludedKwh] = useState(
    settings.electricity_billing.included_kwh_per_night ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMode(settings.electricity_billing.mode);
    setIncludedKwh(settings.electricity_billing.included_kwh_per_night ?? "");
  }, [settings]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body: { mode: ElectricityBillingMode; included_kwh_per_night?: string } = { mode };
      if (mode === "flat_with_included_kwh") {
        body.included_kwh_per_night = includedKwh.trim() || "0";
      }
      await api.patch<TenantSettings>("/api/v1/settings/electricity-billing", body);
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Strom-Abrechnung"
      description="Gilt für alle Buchungen mit Strom. Die Preise selbst pflegen Sie unter Artikel & Preise."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-4">
        <fieldset className="space-y-3" disabled={!canWrite}>
          <legend className="text-sm font-medium text-label">Berechnungsart</legend>
          {(Object.keys(ELECTRICITY_BILLING_MODE_LABELS) as ElectricityBillingMode[]).map((value) => (
            <label
              key={value}
              className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5"
            >
              <input
                type="radio"
                name="electricity_mode"
                value={value}
                checked={mode === value}
                onChange={() => setMode(value)}
                className="mt-1"
              />
              <span className="space-y-1">
                <span className="block text-sm font-medium text-label">
                  {ELECTRICITY_BILLING_MODE_LABELS[value]}
                </span>
                <span className="block text-sm text-label-secondary">{MODE_HINTS[value]}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {mode === "flat_with_included_kwh" ? (
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Inkludierte kWh pro Nacht
            <input
              type="number"
              min="0"
              step="0.1"
              required
              className="eh-input"
              value={includedKwh}
              onChange={(e) => setIncludedKwh(e.target.value)}
              disabled={!canWrite}
            />
          </label>
        ) : null}

        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Strom-Abrechnung gespeichert."
          error={error}
        />
      </form>
    </Card>
  );
}
